import { useMutation } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, useScopedMerchantClient } from "../scoped-merchant-client";

type BulkAddFlagRisk = { riskId: string } | { riskTier: number; riskTypeShort: string };

type BulkAddFlagPayload = {
  videoIds: string[];
  explanation: string;
  policyId?: string;
} & BulkAddFlagRisk;

export const useBulkAddFlag = () => {
  const scope = useScopedMerchantClient();

  const startPolicy = async (videoId: string, flagId: string, policyId: string) => {
    const flagReviews = [
      { flagId, actionTaken: "flag_is_correct" as const, feedbackReason: "Confirmed when the risk was added" },
    ];

    const res = scope.workspaceId
      ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.review[":videoId"].$post({
          param: { ...scope.param, videoId },
          json: { flagReviews, startPolicies: true, policyId },
        })
      : await client.merchant[":merchantId"].assurance.review[":videoId"].$post({
          param: { ...scope.param, videoId },
          json: { flagReviews, startPolicies: true, policyId },
        });

    if (!res.ok) throw await handleError(res);
  };

  return useMutation({
    mutationFn: async (payload: BulkAddFlagPayload) => {
      if (!scope.merchantId || payload.videoIds.length === 0)
        return {
          successful: [] as string[],
          failed: [] as Array<{ videoId: string; error: unknown }>,
          policyFailed: [] as Array<{ videoId: string; error: unknown }>,
        };

      if ("riskId" in payload) {
        const json = {
          videoIds: payload.videoIds,
          explanation: payload.explanation,
          riskId: payload.riskId,
          policyId: payload.policyId,
        };
        const res = scope.workspaceId
          ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.flag.bulk.$post({
              param: scope.param,
              json,
            })
          : await client.merchant[":merchantId"].assurance.flag.bulk.$post({ param: scope.param, json });
        if (!res.ok) {
          const error = await handleError(res);
          return {
            successful: [] as string[],
            failed: payload.videoIds.map((videoId) => ({ videoId, error })),
            policyFailed: [] as Array<{ videoId: string; error: unknown }>,
          };
        }
        const { policyStarted } = await res.json();
        return {
          successful: payload.videoIds,
          failed: [] as Array<{ videoId: string; error: unknown }>,
          policyFailed: policyStarted
            ? []
            : payload.videoIds.map((videoId) => ({ videoId, error: new Error("Policy couldn't be started") })),
        };
      }

      const risk = { riskTier: payload.riskTier, riskTypeShort: payload.riskTypeShort };

      const results = await Promise.allSettled(
        payload.videoIds.map(async (videoId) => {
          try {
            const res = scope.workspaceId
              ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.flag.$post({
                  json: {
                    videoId,
                    explanation: payload.explanation,
                    ...risk,
                  },
                  param: scope.param,
                })
              : await client.merchant[":merchantId"].assurance.flag.$post({
                  json: {
                    videoId,
                    explanation: payload.explanation,
                    ...risk,
                  },
                  param: scope.param,
                });
            if (!res.ok) throw await handleError(res);
            const { flag } = await res.json();

            if (payload.policyId) {
              try {
                await startPolicy(videoId, flag.id, payload.policyId);
              } catch (err) {
                return { videoId, policyStarted: false, error: err };
              }
            }

            return { videoId, policyStarted: true };
          } catch (err) {
            throw { videoId, error: err };
          }
        }),
      );

      const successful: string[] = [];
      const failed: Array<{ videoId: string; error: unknown }> = [];
      const policyFailed: Array<{ videoId: string; error: unknown }> = [];

      for (const result of results) {
        if (result.status === "fulfilled") {
          successful.push(result.value.videoId);
          if (!result.value.policyStarted) {
            policyFailed.push({ videoId: result.value.videoId, error: result.value.error });
          }
        } else {
          const errorData = result.reason as { videoId: string; error: unknown };
          failed.push(errorData);
        }
      }

      return { successful, failed, policyFailed };
    },
    onSuccess: (result) => {
      if (scope.merchantId && result && result.successful.length > 0) {
        queryClient.invalidateQueries({ queryKey: ["video", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["assurance-video", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["video", scope.merchantId, scope.workspaceId, "action-queue"] });
        queryClient.invalidateQueries({ queryKey: ["video", scope.merchantId, scope.workspaceId, "reviewed"] });
      }
    },
  });
};
